import { useState, useEffect } from 'react'
import { Table, Button, Badge, Modal, Form, Row, Col, Card } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { outboundRoutesApi, trunksApi, timeConditionsApi } from '../services/api'
import { useConfirm } from '../components/common/ConfirmDialog'

interface DialPattern {
  id?: string
  pattern: string
  match_type: string
  prepend: string
  prefix: string
  match_cid: string
  priority: number
  is_active: boolean
}

interface OutboundRoute {
  id: string
  name: string
  description: string
  priority: number
  route_type: string
  caller_id_override: string
  caller_id_name_override: string
  pin_required: boolean
  pin: string
  time_condition_id: string
  is_emergency: boolean
  is_active: boolean
  dial_patterns: DialPattern[]
  trunk_ids?: string[]
}

const emptyForm = {
  name: '',
  description: '',
  priority: 100,
  route_type: 'outbound',
  caller_id_override: '',
  caller_id_name_override: '',
  pin_required: false,
  pin: '',
  time_condition_id: '',
  is_emergency: false,
  is_active: true,
  trunk_ids: [] as string[],
  dial_patterns: [] as DialPattern[]
}

const emptyDialPattern: DialPattern = {
  pattern: '',
  match_type: 'pattern',
  prepend: '',
  prefix: '',
  match_cid: '',
  priority: 1,
  is_active: true
}

const routeTypes = [
  { value: 'outbound', label: 'Standard ausgehend' },
  { value: 'emergency', label: 'Notruf' },
  { value: 'international', label: 'International' },
  { value: 'local', label: 'Ortsgespräch' },
  { value: 'mobile', label: 'Mobilfunk' },
  { value: 'toll_free', label: 'Gebührenfrei' }
]

const matchTypes = [
  { value: 'pattern', label: 'Muster' },
  { value: 'exact', label: 'Exakt' },
  { value: 'prefix', label: 'Präfix' }
]

export default function OutboundRoutes() {
  const confirm = useConfirm()
  const [routes, setRoutes] = useState<OutboundRoute[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingRoute, setEditingRoute] = useState<OutboundRoute | null>(null)
  const [formData, setFormData] = useState(emptyForm)

  const [trunks, setTrunks] = useState<{id: string, name: string}[]>([])
  const [timeConditions, setTimeConditions] = useState<{id: string, name: string}[]>([])

  useEffect(() => {
    loadRoutes()
    loadRelatedData()
  }, [])

  const loadRoutes = async () => {
    try {
      const response = await outboundRoutesApi.list()
      setRoutes(response.data.items || [])
    } catch {
      toast.error('Fehler beim Laden')
    } finally {
      setLoading(false)
    }
  }

  const loadRelatedData = async () => {
    try {
      const [trunkRes, tcRes] = await Promise.all([
        trunksApi.list(),
        timeConditionsApi.list()
      ])
      setTrunks(trunkRes.data.items || [])
      setTimeConditions(tcRes.data.items || [])
    } catch {
      console.error('Error loading related data')
    }
  }

  const openCreateModal = () => {
    setEditingRoute(null)
    setFormData(emptyForm)
    setShowModal(true)
  }

  const openEditModal = (route: OutboundRoute) => {
    setEditingRoute(route)
    setFormData({
      name: route.name,
      description: route.description || '',
      priority: route.priority || 100,
      route_type: route.route_type || 'outbound',
      caller_id_override: route.caller_id_override || '',
      caller_id_name_override: route.caller_id_name_override || '',
      pin_required: route.pin_required ?? false,
      pin: '',
      time_condition_id: route.time_condition_id || '',
      is_emergency: route.is_emergency ?? false,
      is_active: route.is_active,
      trunk_ids: route.trunk_ids || [],
      dial_patterns: route.dial_patterns || []
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const submitData: Record<string, unknown> = { ...formData }
      if (!submitData.pin) delete submitData.pin
      if (!submitData.time_condition_id) delete submitData.time_condition_id

      if (editingRoute) {
        await outboundRoutesApi.update(editingRoute.id, submitData)
        toast.success('Route aktualisiert')
      } else {
        await outboundRoutesApi.create(submitData)
        toast.success('Route erstellt')
      }
      closeModal()
      loadRoutes()
    } catch {
      toast.error('Fehler beim Speichern')
    }
  }

  const handleDelete = async (route: OutboundRoute) => {
    const confirmed = await confirm({
      title: 'Route löschen',
      message: `Route "${route.name}" wirklich löschen?`,
      confirmText: 'Löschen',
      variant: 'danger'
    })
    if (!confirmed) return
    try {
      await outboundRoutesApi.delete(route.id)
      toast.success('Route gelöscht')
      loadRoutes()
    } catch {
      toast.error('Fehler beim Löschen')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingRoute(null)
    setFormData(emptyForm)
  }

  const addDialPattern = () => {
    setFormData({
      ...formData,
      dial_patterns: [...formData.dial_patterns, { ...emptyDialPattern }]
    })
  }

  const updateDialPattern = (index: number, field: string, value: unknown) => {
    const newPatterns = [...formData.dial_patterns]
    newPatterns[index] = { ...newPatterns[index], [field]: value }
    setFormData({ ...formData, dial_patterns: newPatterns })
  }

  const removeDialPattern = (index: number) => {
    setFormData({
      ...formData,
      dial_patterns: formData.dial_patterns.filter((_, i) => i !== index)
    })
  }

  const toggleTrunk = (trunkId: string) => {
    const newTrunkIds = formData.trunk_ids.includes(trunkId)
      ? formData.trunk_ids.filter(id => id !== trunkId)
      : [...formData.trunk_ids, trunkId]
    setFormData({ ...formData, trunk_ids: newTrunkIds })
  }

  const moveTrunk = (index: number, direction: 'up' | 'down') => {
    const newTrunkIds = [...formData.trunk_ids]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= newTrunkIds.length) return
    [newTrunkIds[index], newTrunkIds[newIndex]] = [newTrunkIds[newIndex], newTrunkIds[index]]
    setFormData({ ...formData, trunk_ids: newTrunkIds })
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Ausgehende Routen</h4>
        <Button variant="primary" onClick={openCreateModal}>
          <i className="bi bi-plus-lg me-1"></i> Neue Route
        </Button>
      </div>

      <div className="table-container">
        <Table hover className="mb-0">
          <thead>
            <tr>
              <th>Priorität</th>
              <th>Name</th>
              <th>Typ</th>
              <th>Muster</th>
              <th>Trunks</th>
              <th>Status</th>
              <th className="text-end">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-4">Laden...</td></tr>
            ) : routes.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-4 text-muted">Keine Routen</td></tr>
            ) : (
              routes.map(r => (
                <tr key={r.id}>
                  <td><Badge bg="secondary">{r.priority}</Badge></td>
                  <td>
                    <strong>{r.name}</strong>
                    {r.is_emergency && <Badge bg="danger" className="ms-2">Notruf</Badge>}
                  </td>
                  <td><Badge bg="info">{routeTypes.find(rt => rt.value === r.route_type)?.label || r.route_type}</Badge></td>
                  <td><Badge bg="secondary">{(r.dial_patterns || []).length} Muster</Badge></td>
                  <td><Badge bg="secondary">{(r.trunk_ids || []).length} Trunks</Badge></td>
                  <td><Badge bg={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Aktiv' : 'Inaktiv'}</Badge></td>
                  <td className="text-end">
                    <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEditModal(r)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(r)}>
                      <i className="bi bi-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingRoute ? 'Route bearbeiten' : 'Neue Route'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="z.B. Ausgehende Anrufe"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Priorität</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 100})}
                    min={1}
                    max={9999}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Typ</Form.Label>
                  <Form.Select
                    value={formData.route_type}
                    onChange={(e) => setFormData({...formData, route_type: e.target.value})}
                  >
                    {routeTypes.map(rt => (
                      <option key={rt.value} value={rt.value}>{rt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Beschreibung</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optionale Beschreibung"
              />
            </Form.Group>

            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>Dial-Muster</span>
                <Button variant="outline-primary" size="sm" onClick={addDialPattern}>
                  <i className="bi bi-plus-lg me-1"></i> Muster hinzufügen
                </Button>
              </Card.Header>
              <Card.Body>
                {formData.dial_patterns.length === 0 ? (
                  <p className="text-muted mb-0">Keine Dial-Muster definiert. Fügen Sie mindestens ein Muster hinzu.</p>
                ) : (
                  formData.dial_patterns.map((pattern, index) => (
                    <Card key={index} className="mb-2">
                      <Card.Body className="py-2">
                        <Row className="align-items-end">
                          <Col md={3}>
                            <Form.Group>
                              <Form.Label className="small">Muster *</Form.Label>
                              <Form.Control
                                size="sm"
                                value={pattern.pattern}
                                onChange={(e) => updateDialPattern(index, 'pattern', e.target.value)}
                                placeholder="z.B. _0X., _00X."
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small">Match-Typ</Form.Label>
                              <Form.Select
                                size="sm"
                                value={pattern.match_type}
                                onChange={(e) => updateDialPattern(index, 'match_type', e.target.value)}
                              >
                                {matchTypes.map(mt => (
                                  <option key={mt.value} value={mt.value}>{mt.label}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small">Präfix entfernen</Form.Label>
                              <Form.Control
                                size="sm"
                                value={pattern.prefix}
                                onChange={(e) => updateDialPattern(index, 'prefix', e.target.value)}
                                placeholder="z.B. 0"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small">Voranstellen</Form.Label>
                              <Form.Control
                                size="sm"
                                value={pattern.prepend}
                                onChange={(e) => updateDialPattern(index, 'prepend', e.target.value)}
                                placeholder="z.B. +49"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Group>
                              <Form.Label className="small">CID-Match</Form.Label>
                              <Form.Control
                                size="sm"
                                value={pattern.match_cid}
                                onChange={(e) => updateDialPattern(index, 'match_cid', e.target.value)}
                                placeholder="Optional"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={1}>
                            <Button variant="outline-danger" size="sm" onClick={() => removeDialPattern(index)}>
                              <i className="bi bi-trash"></i>
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Trunks (Reihenfolge = Priorität)</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p className="small text-muted mb-2">Verfügbare Trunks:</p>
                    {trunks.filter(t => !formData.trunk_ids.includes(t.id)).map(trunk => (
                      <Button
                        key={trunk.id}
                        variant="outline-secondary"
                        size="sm"
                        className="me-1 mb-1"
                        onClick={() => toggleTrunk(trunk.id)}
                      >
                        <i className="bi bi-plus-lg me-1"></i> {trunk.name}
                      </Button>
                    ))}
                    {trunks.filter(t => !formData.trunk_ids.includes(t.id)).length === 0 && (
                      <span className="text-muted">Keine weiteren Trunks verfügbar</span>
                    )}
                  </Col>
                  <Col md={6}>
                    <p className="small text-muted mb-2">Ausgewählte Trunks (Failover-Reihenfolge):</p>
                    {formData.trunk_ids.length === 0 ? (
                      <span className="text-muted">Keine Trunks ausgewählt</span>
                    ) : (
                      formData.trunk_ids.map((trunkId, index) => {
                        const trunk = trunks.find(t => t.id === trunkId)
                        return (
                          <div key={trunkId} className="d-flex align-items-center mb-1">
                            <Badge bg="primary" className="me-2">{index + 1}</Badge>
                            <span className="me-auto">{trunk?.name || trunkId}</span>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-1"
                              onClick={() => moveTrunk(index, 'up')}
                              disabled={index === 0}
                            >
                              <i className="bi bi-arrow-up"></i>
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-1"
                              onClick={() => moveTrunk(index, 'down')}
                              disabled={index === formData.trunk_ids.length - 1}
                            >
                              <i className="bi bi-arrow-down"></i>
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-danger"
                              onClick={() => toggleTrunk(trunkId)}
                            >
                              <i className="bi bi-x-lg"></i>
                            </Button>
                          </div>
                        )
                      })
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Anrufer-ID überschreiben</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anrufer-ID Nummer</Form.Label>
                      <Form.Control
                        value={formData.caller_id_override}
                        onChange={(e) => setFormData({...formData, caller_id_override: e.target.value})}
                        placeholder="z.B. +4930123456"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anrufer-ID Name</Form.Label>
                      <Form.Control
                        value={formData.caller_id_name_override}
                        onChange={(e) => setFormData({...formData, caller_id_name_override: e.target.value})}
                        placeholder="z.B. Firma GmbH"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Header>Erweiterte Optionen</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Zeitbedingung</Form.Label>
                      <Form.Select
                        value={formData.time_condition_id}
                        onChange={(e) => setFormData({...formData, time_condition_id: e.target.value})}
                      >
                        <option value="">-- Keine --</option>
                        {timeConditions.map(tc => (
                          <option key={tc.id} value={tc.id}>{tc.name}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">Route nur zu bestimmten Zeiten aktiv</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="pin_required"
                        label="PIN erforderlich"
                        checked={formData.pin_required}
                        onChange={(e) => setFormData({...formData, pin_required: e.target.checked})}
                      />
                    </Form.Group>
                    {formData.pin_required && (
                      <Form.Group className="mb-3">
                        <Form.Label>{editingRoute ? 'Neuer PIN' : 'PIN'}</Form.Label>
                        <Form.Control
                          type="password"
                          value={formData.pin}
                          onChange={(e) => setFormData({...formData, pin: e.target.value})}
                          placeholder="z.B. 1234"
                        />
                      </Form.Group>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="is_emergency"
                        label="Notruf-Route"
                        checked={formData.is_emergency}
                        onChange={(e) => setFormData({...formData, is_emergency: e.target.checked})}
                      />
                      <Form.Text className="text-muted">Umgeht alle Einschränkungen</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="switch"
                        id="is_active"
                        label="Aktiv"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Abbrechen</Button>
            <Button variant="primary" type="submit">
              {editingRoute ? 'Speichern' : 'Erstellen'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}
